import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  MDBRow,
  MDBCol,
  MDBCard,
  MDBCardBody,
  MDBBtn,
  MDBIcon,
  MDBSpinner,
} from "mdb-react-ui-kit";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  Line,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import { getMfmSystemStatus, reprocessMfmLibrary } from "Projects/CC_cubi_clan_project/Utils/MfmService";
import "../MFMHomeView/MFMHomeView.css";

const DASH_COLORS = ["#0b6bcb", "#18a999", "#f2a541", "#e85f5c", "#7a6ff0", "#4b5563"];

function parseDate(value) {
  if (!value) return null;
  const d = new Date(String(value).replace(/\[[^\]]+\]$/, ""));
  return Number.isNaN(d.getTime()) ? null : d;
}

function formatDayLabel(day) {
  const d = parseDate(day);
  if (!d) return day;
  return d.toLocaleDateString(undefined, { day: "2-digit", month: "2-digit" });
}

function buildSeries(uploadsByDay = [], deletionsByDay = [], queueByDay = []) {
  const byDay = new Map();
  const add = (rows, key) => {
    rows.forEach((r) => {
      const day = String(r?.day || "");
      if (!day) return;
      if (!byDay.has(day)) byDay.set(day, { day, uploads: 0, deleted: 0, queued: 0 });
      byDay.get(day)[key] = Number(r?.[key] || 0);
    });
  };
  add(uploadsByDay, "uploads");
  add(deletionsByDay, "deleted");
  add(queueByDay, "queued");

  return Array.from(byDay.values())
    .sort((a, b) => String(a.day).localeCompare(String(b.day)))
    .slice(-30)
    .map((row) => ({ ...row, dayLabel: formatDayLabel(row.day) }));
}

function KpiCard({ title, value, hint, tone = "neutral", icon = "chart-bar" }) {
  return (
    <MDBCard className={`mfm-kpi-card mfm-kpi-card--${tone} h-100`}>
      <MDBCardBody>
        <div className="mfm-kpi-head">
          <small>{title}</small>
          <MDBIcon fas icon={icon} />
        </div>
        <div className="mfm-kpi-value">{value}</div>
        <small className="text-muted">{hint}</small>
      </MDBCardBody>
    </MDBCard>
  );
}

export default function MFMStatusView({ appCode, selectedLibrary }) {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [reprocessLoading, setReprocessLoading] = useState(false);
  const [reprocessInfo, setReprocessInfo] = useState(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const status = await getMfmSystemStatus(appCode);
      setData(status || null);
    } catch (_) {
      setError("No se pudo cargar el estado del sistema.");
    } finally {
      setLoading(false);
    }
  }, [appCode]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const summary = data?.summary || {};
  const queue = data?.queue || {};
  const activitySeries = useMemo(
    () => buildSeries(data?.uploadsByDay || [], data?.deletionsByDay || [], data?.queueByDay || []),
    [data]
  );
  const filesByApp = data?.filesByApp || [];
  const statusBreakdown = data?.statusBreakdown || [];
  const topLibraries = data?.topLibraries || [];

  const queuePie = [
    { name: "Pendiente", value: Number(queue.pending || 0) },
    { name: "En progreso", value: Number(queue.inProgress || 0) },
    { name: "Fallidos", value: Number(queue.failed || 0) },
    { name: "Completados", value: Number(queue.completed || 0) },
  ];

  const effectiveAppCode = selectedLibrary?.appCode || appCode;
  const targetLibraryId = selectedLibrary?.type === "library" ? selectedLibrary?.idLibrary : null;
  const targetLabel = targetLibraryId
    ? `biblioteca ${selectedLibrary?.name || "seleccionada"}`
    : (effectiveAppCode ? `app ${effectiveAppCode}` : "todo el sistema");

  const handleReprocess = async () => {
    const confirm = window.confirm(`¿Reprocesar ${targetLabel} y recalcular coherencias MFM?`);
    if (!confirm) return;

    const payload = { appCode: effectiveAppCode, libraryId: targetLibraryId || undefined };
    // Log temporal para diagnóstico end-to-end del reproceso manual.
    console.info("[TMP_DEBUG_REPROCESS_UI] click", {
      targetLabel,
      effectiveAppCode,
      targetLibraryId,
      payload,
    });

    setReprocessLoading(true);
    setReprocessInfo(null);
    try {
      const result = await reprocessMfmLibrary(payload);
      console.info("[TMP_DEBUG_REPROCESS_UI] success", result);
      setReprocessInfo({
        ok: true,
        message: `Reproceso completado: ${Number(result?.scannedFiles || 0)} fichero(s), ${Number(result?.transcodeRecovered || 0)} job(s) recuperado(s), ${Number(result?.transcodeEnqueued || 0)} job(s) encolado(s), ${Number(result?.fileStatusFixed || 0)} estado(s) corregido(s).`,
      });
      await loadData();
    } catch (err) {
      console.error("[TMP_DEBUG_REPROCESS_UI] error", {
        message: err?.message,
        error: err,
      });
      setReprocessInfo({
        ok: false,
        message: err?.message || "No se pudo ejecutar el reproceso.",
      });
    } finally {
      setReprocessLoading(false);
    }
  };

  return (
    <div className="mfm-home p-4">
      <MDBRow className="mb-3 align-items-center">
        <MDBCol md="8">
          <h5 className="mb-0"><MDBIcon fas icon="gauge-high" className="me-2" />Estado del sistema MFM</h5>
          <small className="text-muted">Resumen operativo e histórico extraído en tiempo real sin modificar la BBDD.</small>
        </MDBCol>
        <MDBCol md="4" className="text-md-end mt-2 mt-md-0 d-flex justify-content-md-end gap-2">
          <MDBBtn size="sm" color="dark" onClick={handleReprocess} disabled={reprocessLoading}>
            {reprocessLoading ? <MDBSpinner size="sm" className="me-1" /> : <MDBIcon fas icon="screwdriver-wrench" className="me-1" />}
            Reprocesar
          </MDBBtn>
          <MDBBtn size="sm" color="light" onClick={loadData}>
            <MDBIcon fas icon="arrows-rotate" className="me-1" />Actualizar
          </MDBBtn>
        </MDBCol>
      </MDBRow>

      {reprocessInfo && (
        <MDBCard className={`mb-3 ${reprocessInfo.ok ? "border-success" : "mfm-status-error-card"}`}>
          <MDBCardBody>
            <MDBIcon fas icon={reprocessInfo.ok ? "circle-check" : "triangle-exclamation"} className="me-2" />
            {reprocessInfo.message}
          </MDBCardBody>
        </MDBCard>
      )}

      {loading && (
        <div className="text-center py-4"><MDBSpinner className="me-2" />Cargando estado...</div>
      )}

      {error && (
        <MDBCard className="mfm-status-error-card mb-3">
          <MDBCardBody>
            <MDBIcon fas icon="triangle-exclamation" className="me-2" />{error}
          </MDBCardBody>
        </MDBCard>
      )}

      {!loading && !error && (
        <>
          <MDBRow className="g-3 mb-3">
            <MDBCol xs="12" sm="6" lg="3"><KpiCard title="Ficheros totales" value={Number(summary.totalFiles || 0)} hint="Inventario actual" tone="primary" icon="database" /></MDBCol>
            <MDBCol xs="12" sm="6" lg="3"><KpiCard title="En proceso" value={Number(summary.processingFiles || 0)} hint="Estado processing" tone={Number(summary.processingFiles || 0) > 0 ? "warning" : "neutral"} icon="cogs" /></MDBCol>
            <MDBCol xs="12" sm="6" lg="3"><KpiCard title="Eliminados" value={Number(summary.deletedFiles || 0)} hint="En papelera global" tone="danger" icon="trash" /></MDBCol>
            <MDBCol xs="12" sm="6" lg="3"><KpiCard title="Pendientes" value={Number(queue.pending || 0)} hint="Cola de transcodificación" tone={Number(queue.pending || 0) > 0 ? "warning" : "ok"} icon="clock" /></MDBCol>
          </MDBRow>

          <MDBRow className="g-3 mb-3">
            <MDBCol xs="12" xl="8">
              <MDBCard className="h-100">
                <MDBCardBody>
                  <h6 className="mb-3">Actividad diaria (30 días)</h6>
                  <div className="mfm-chart-box">
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={activitySeries}>
                        <defs>
                          <linearGradient id="mfmUploadsStandalone" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#0b6bcb" stopOpacity={0.35} />
                            <stop offset="95%" stopColor="#0b6bcb" stopOpacity={0.03} />
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="dayLabel" />
                        <YAxis allowDecimals={false} />
                        <Tooltip />
                        <Legend />
                        <Area type="monotone" dataKey="uploads" stroke="#0b6bcb" fill="url(#mfmUploadsStandalone)" strokeWidth={2} name="Subidas" />
                        <Line type="monotone" dataKey="deleted" stroke="#e85f5c" strokeWidth={2} name="Eliminados" dot={false} />
                        <Line type="monotone" dataKey="queued" stroke="#f2a541" strokeWidth={2} name="Encolados" dot={false} />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                </MDBCardBody>
              </MDBCard>
            </MDBCol>
            <MDBCol xs="12" xl="4">
              <MDBCard className="h-100">
                <MDBCardBody>
                  <h6 className="mb-3">Estado de cola</h6>
                  <div className="mfm-chart-box mfm-chart-box--small">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie data={queuePie} dataKey="value" nameKey="name" innerRadius={54} outerRadius={88} paddingAngle={2}>
                          {queuePie.map((entry, idx) => <Cell key={entry.name} fill={DASH_COLORS[idx % DASH_COLORS.length]} />)}
                        </Pie>
                        <Tooltip />
                        <Legend verticalAlign="bottom" height={36} />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                </MDBCardBody>
              </MDBCard>
            </MDBCol>
          </MDBRow>

          <MDBRow className="g-3">
            <MDBCol xs="12" xl="6">
              <MDBCard className="h-100">
                <MDBCardBody>
                  <h6 className="mb-3">Ficheros por app</h6>
                  <div className="mfm-chart-box mfm-chart-box--small">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={filesByApp} layout="vertical" margin={{ left: 8, right: 20 }}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis type="number" allowDecimals={false} />
                        <YAxis type="category" dataKey="appCode" width={60} />
                        <Tooltip />
                        <Bar dataKey="files" fill="#18a999" radius={[0, 6, 6, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </MDBCardBody>
              </MDBCard>
            </MDBCol>
            <MDBCol xs="12" xl="6">
              <MDBCard className="h-100">
                <MDBCardBody>
                  <h6 className="mb-3">Distribución por estado</h6>
                  <div className="mfm-chart-box mfm-chart-box--small">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={statusBreakdown}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="status" />
                        <YAxis allowDecimals={false} />
                        <Tooltip />
                        <Bar dataKey="count" fill="#4c6ef5" radius={[6, 6, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </MDBCardBody>
              </MDBCard>
            </MDBCol>
          </MDBRow>

          <MDBRow className="g-3 mt-1">
            <MDBCol xs="12">
              <MDBCard>
                <MDBCardBody>
                  <h6 className="mb-3">Top bibliotecas por volumen</h6>
                  <div className="table-responsive">
                    <table className="table table-sm align-middle mfm-status-table">
                      <thead>
                        <tr>
                          <th>App</th>
                          <th>Biblioteca</th>
                          <th className="text-end">Ficheros</th>
                        </tr>
                      </thead>
                      <tbody>
                        {topLibraries.length === 0 && (
                          <tr><td colSpan="3" className="text-center text-muted py-3">Sin datos</td></tr>
                        )}
                        {topLibraries.map((row, idx) => (
                          <tr key={`${row.appCode || "-"}-${row.libraryName || "-"}-${idx}`}>
                            <td>{row.appCode || "-"}</td>
                            <td>{row.libraryName || "-"}</td>
                            <td className="text-end fw-semibold">{Number(row.files || 0)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </MDBCardBody>
              </MDBCard>
            </MDBCol>
          </MDBRow>
        </>
      )}
    </div>
  );
}
